/**
 * Who agreed to trade places with whom.
 *
 * Two participants exchanging their places on the ring is the one way a
 * delegated participant influences their own coordinate, so the pairing is
 * room state while the arithmetic that turns it into a spot is not — that is
 * `src/engine/scene/placement.ts`.
 */

/** peerId -> peerId, symmetric. */
export type SwapMap = Record<string, string>;

/** Records a swap, dropping whatever either peer was paired with before. */
export function applySwap(swaps: SwapMap, a: string, b: string): SwapMap {
  if (a === b) return swaps;
  const next: SwapMap = {};
  Object.entries(swaps).forEach(([from, to]) => {
    if (from === a || from === b || to === a || to === b) return;
    next[from] = to;
  });
  next[a] = b;
  next[b] = a;
  return next;
}

/** Forgets every pair the peer took part in. */
export function forgetSwaps(swaps: SwapMap, peerId: string): SwapMap {
  const next: SwapMap = {};
  Object.entries(swaps).forEach(([from, to]) => {
    if (from === peerId || to === peerId) return;
    next[from] = to;
  });
  return next;
}

/** True while the peer is part of a pair. */
export function hasSwap(swaps: SwapMap, peerId: string): boolean {
  return peerId in swaps;
}

/**
 * The roster in the order the ring is filled: join order, with every agreed
 * pair exchanged. Pairs are applied in sorted key order so the result does not
 * depend on how the map was built.
 */
export function orderWithSwaps(
  orderedPeerIds: readonly string[],
  swaps: SwapMap,
): string[] {
  const order = [...orderedPeerIds];
  const index = new Map(order.map((id, i) => [id, i]));
  const done = new Set<string>();
  Object.keys(swaps)
    .sort()
    .forEach((a) => {
      const b = swaps[a];
      if (done.has(a) || done.has(b)) return;
      const ia = index.get(a);
      const ib = index.get(b);
      if (ia === undefined || ib === undefined) return;
      order[ia] = b;
      order[ib] = a;
      done.add(a);
      done.add(b);
    });
  return order;
}
